import { LOCALES, READY_LOCALES, useI18n, type Locale } from '@/i18n'
import { cn } from '@/lib/utils'

const LOCALE_LABEL: Record<Locale, 'lang.de' | 'lang.en' | 'lang.fr'> = {
  de: 'lang.de',
  en: 'lang.en',
  fr: 'lang.fr',
}

/**
 * Locale switch. FR is listed but visibly marked "ausstehend" — the scaffolding
 * is there, the translation is not, and the UI says so instead of pretending.
 */
export function LocaleSwitch({ className }: { className?: string }) {
  const { locale, setLocale, t } = useI18n()

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* The group below carries the same string as its `aria-label`, so hiding
          the visible copy below `sm` costs a screen reader nothing and buys the
          phone header the width it needs. */}
      <span className="hidden text-[12px] text-muted sm:inline">{t('lang.label')}</span>
      <div role="group" aria-label={t('lang.label')} className="flex rounded-pill border border-border p-0.5">
        {LOCALES.map((code) => {
          const ready = READY_LOCALES.includes(code)
          const active = locale === code
          return (
            <button
              key={code}
              type="button"
              onClick={() => setLocale(code)}
              aria-pressed={active}
              className={cn(
                'rounded-pill px-3 py-1 text-[12px] font-medium transition-colors duration-fast ease-enter',
                active ? 'bg-ink text-white' : 'text-ink-2 hover:bg-surface',
                !ready && 'italic',
              )}
            >
              {code.toUpperCase()}
              {!ready ? (
                /* `max-sm:sr-only`, not `hidden`: the word is still announced
                   and still in the FR banner — it just stops costing the phone
                   header 90 px of width. */
                <span className="ml-1 font-normal opacity-80 max-sm:sr-only">
                  ({t('lang.frPending')})
                </span>
              ) : null}
              <span className="sr-only"> — {t(LOCALE_LABEL[code])}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
